/**
 * React Query Hooks for the Rule Book
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ruleService, FinanceRule, FinanceRuleInput, PlaybookOverrideFields, RuleSuggestionStatus } from '../../services/api/ruleService';
import { queryKeys } from '../../lib/queryClient';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Hook to fetch every rule and the categories in use
 */
export function useRules() {
  const { token } = useAuth();

  return useQuery({
    queryKey: queryKeys.rules.list(),
    queryFn: async () => {
      if (!token) throw new Error('No authentication token');
      return ruleService.getAll(token);
    },
    enabled: !!token,
    // Rules are edited by other people and change what analyses report, so
    // keep the list fresh rather than using the long static-data stale time.
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to fetch a rule's revision history
 */
export function useRuleHistory(ruleId: string | undefined) {
  const { token } = useAuth();

  return useQuery({
    queryKey: queryKeys.rules.history(ruleId || ''),
    queryFn: async () => {
      if (!token) throw new Error('No authentication token');
      return ruleService.history(token, ruleId as string);
    },
    enabled: !!token && !!ruleId,
    // Always refetch on open: a history fetched while an edit was still saving would otherwise be served stale
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

function useRuleMutation<TVars, TResult = unknown>(fn: (token: string, vars: TVars) => Promise<TResult>) {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: TVars) => {
      if (!token) throw new Error('No authentication token');
      return fn(token, vars);
    },
    // Never retry: a conflict or validation error won't succeed on a second try,
    // and retrying a create after a dropped response could add the rule twice.
    retry: false,
    onSuccess: (result) => {
      // Put a returned rule straight into the list, so its new version is known
      // before the refetch lands (otherwise an edit opened right after a toggle
      // is sent with the old version and rejected as a conflict).
      const rule = result as FinanceRule | undefined;
      if (rule && typeof rule === 'object' && '_id' in rule && 'version' in rule) {
        queryClient.setQueryData<{ rules: FinanceRule[]; categories: string[] }>(queryKeys.rules.list(), old =>
          old ? { ...old, rules: old.rules.map(r => (r._id === rule._id ? { ...r, ...rule, createdBy: r.createdBy, updatedBy: r.updatedBy } : r)) } : old
        );
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.rules.all });
    },
  });
}

export const useCreateRule = () =>
  useRuleMutation((token, input: FinanceRuleInput) => ruleService.create(token, input));

export const useUpdateRule = () =>
  useRuleMutation((token, { id, input }: { id: string; input: FinanceRuleInput }) =>
    ruleService.update(token, id, input));

export const useToggleRule = () =>
  useRuleMutation((token, { id, enabled }: { id: string; enabled: boolean }) =>
    ruleService.toggle(token, id, enabled));

export const useDeleteRule = () =>
  useRuleMutation((token, id: string) => ruleService.delete(token, id));

/**
 * Hook to fetch the playbooks finance can tune
 */
export function usePlaybooks() {
  const { token } = useAuth();

  return useQuery({
    queryKey: queryKeys.rules.playbooks(),
    queryFn: async () => {
      if (!token) throw new Error('No authentication token');
      return ruleService.getPlaybooks(token);
    },
    enabled: !!token,
  });
}

/**
 * Hook to fetch a playbook's parameters, defaults and current overrides
 */
export function usePlaybookThresholds(playbookId: string | undefined) {
  const { token } = useAuth();

  return useQuery({
    queryKey: queryKeys.rules.playbook(playbookId || ''),
    queryFn: async () => {
      if (!token) throw new Error('No authentication token');
      return ruleService.getPlaybookThresholds(token, playbookId as string);
    },
    enabled: !!token && !!playbookId,
    staleTime: 30 * 1000,
  });
}

export const useSavePlaybookOverride = () =>
  useRuleMutation((token, v: { playbookId: string; parameterId: string; fields: PlaybookOverrideFields & { version?: number } }) =>
    ruleService.saveOverride(token, v.playbookId, v.parameterId, v.fields));

export const useResetPlaybookOverride = () =>
  useRuleMutation((token, v: { playbookId: string; parameterId: string }) =>
    ruleService.resetOverride(token, v.playbookId, v.parameterId));

/**
 * Hook to fetch rule suggestions with a given status, plus the pending count
 */
export function useRuleSuggestions(status: RuleSuggestionStatus = 'pending') {
  const { token } = useAuth();

  return useQuery({
    queryKey: queryKeys.rules.suggestions(status),
    queryFn: async () => {
      if (!token) throw new Error('No authentication token');
      return ruleService.getSuggestions(token, status);
    },
    enabled: !!token,
    staleTime: 30 * 1000,
  });
}

// Accepting creates a rule; all of these invalidate the whole rule book (rules + suggestions)
export const useAcceptSuggestion = () =>
  useRuleMutation((token, v: { id: string; input: FinanceRuleInput }) => ruleService.acceptSuggestion(token, v.id, v.input));

export const useDismissSuggestion = () =>
  useRuleMutation((token, v: { id: string; reason?: string }) => ruleService.dismissSuggestion(token, v.id, v.reason));

export const useRestoreSuggestion = () =>
  useRuleMutation((token, id: string) => ruleService.restoreSuggestion(token, id));

/** Preview or apply a CSV import of rules. */
export const useImportRules = () =>
  useRuleMutation((token, v: { csv: string; apply: boolean }) => ruleService.importRules(token, v.csv, v.apply));
